import React, { useState } from 'react';
import { Carousel, Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import './style.css';
import { useSmallScreen } from 'helpers/isSmallScreen';
import Typography from 'helpers/fonts';
import SummerwoodSVG from 'components/summerwoodsvg/summerwood-svg';
import { Product, StyleListModel } from 'api';
import { getImageUrl } from 'helpers/selectExistingImage';
import LoadingImage from './loadingImage';
import { motion } from 'framer-motion';
import { UI } from 'helpers/constants';

interface ProductStyleItemProps {
  text: string;
  description?: string;
  iconUrl?: any;
  src: string | Array<string>; // image src
  onSelectItem: () => void;
  id: string;
}

// need to overwrite styles from antd as they hide arrows by default
const PrevArrow = (props: any) => {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{
        ...style,
        color: 'white',
        fontSize: '24px',
        left: 5,
        zIndex: 100,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <LeftOutlined />
    </div>
  );
};

const NextArrow = (props: any) => {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{
        ...style,
        color: 'white',
        fontSize: '24px',
        right: 10,
        zIndex: 100,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}    >
      <RightOutlined style={{}} />
    </div>
  );
};

const ProductStyleItem: React.FunctionComponent<ProductStyleItemProps> = ({
  text,
  src,
  description,
  onSelectItem,
  iconUrl,
  id,
}: ProductStyleItemProps) => {
  const variants = {
    hide: { translateY: 5000, opacity: 0 },
    show: {
      opacity: 1,
      translateY: 0,
      transition: { duration: 0.4, ease: UI.EASE_QUINT },
    },
  };
  const [busy, setBusy] = useState<string | undefined>(undefined);

  return (
    <motion.div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '320px',
        cursor: 'pointer',
      }}
      key={text}
      variants={variants}
      onClick={() => {
        if (busy === undefined) {
          try {
            setBusy(id);
            onSelectItem();
          } catch (error) {
            setBusy(undefined);
          }
        }
      }}
    >
      {Array.isArray(src) ? (
        // <div
        //   onClick={(e) => {
        //     console.log('e');

        //     // e.stopPropagation();
        //   }}
        // >
        <Carousel arrows nextArrow={<NextArrow />} prevArrow={<PrevArrow />}>
          {src.map((s) => (
            <LoadingImage src={s} key={s} loading={busy === id} />
          ))}
        </Carousel>
      ) : (
        // </div>
        <LoadingImage src={src} loading={busy === id} />
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: iconUrl ? '1fr 8fr 1fr' : '9fr 1fr',
          gridGap: '12px',
          gridTemplateRows: '20px',
          alignItems: 'center',
          margin: '16px 4px',
        }}
      >
        {iconUrl && <img alt="icon" src={iconUrl} style={{ maxWidth: '30px', maxHeight: '30px' }} />}
        <Typography styleid={2}>{text}</Typography>
        <Button type="link" style={{ padding: 0 }}>
          <SummerwoodSVG
            type="arrowRightButton"
            style={{ justifySelf: 'end' }}
          />
        </Button>
      </div>
      {description && (
        <Typography styleid={6} style={{ lineHeight: '18px' }}>
          {description}
        </Typography>
      )}
    </motion.div>
  );
};

export interface ProductStyleListProps {
  items: Array<Product> | Array<StyleListModel>;
  onSelectItem: (id: string) => void;
}
const isProductArray = (
  arr: Array<Product> | Array<StyleListModel>
): arr is Array<Product> => {
  return (arr as Array<Product>)[0].iconUrl !== undefined;
};

const ProductStyleList: React.FunctionComponent<ProductStyleListProps> = ({
  items,
  onSelectItem,
}) => {
  const isSmallScreen = useSmallScreen();
  const variants = {
    show: { opacity: 1, transition: { duration: 0.07, staggerChildren: 0.05 } },
    hide: { opacity: 0 },
  };

  return (
    <>
      {items && (
        <motion.div
          style={{
            display: 'grid',
            gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr 1fr',
            columnGap: '40px',
            rowGap: '50px',
            alignItems: 'center',
            margin: isSmallScreen ? '0 0 24px' : '40px 0 24px',
          }}
          variants={variants}
          initial="hide"
          animate="show"
        >
          {isProductArray(items)
            ? items.map((i) => (
                <ProductStyleItem
                  id={i.id}
                  text={i.title}
                  description={i.description}
                  iconUrl={i.iconUrl}
                  src={getImageUrl(i.thumbnail)}
                  onSelectItem={() => onSelectItem(i.id)}
                  key={i.id}
                />
              ))
            : items.map((i) => (
                <ProductStyleItem
                  id={i.id}
                  text={i.title}
                  description={i.description}
                  src={i.thumbnails.map((t) => getImageUrl(t))}
                  onSelectItem={() => onSelectItem(i.id)}
                  key={i.id}
                />
              ))}
        </motion.div>
      )}
    </>
  );
};

export default ProductStyleList;
